import { useState, useEffect, useCallback } from "react";
import { fetchEgdGames, fetchEgdJackpotMeters } from "src/api/payload/egd";
import { isPayloadEgdSource } from "src/utils/egdSource";

/**
 * Loads EGD games and jackpot meters from Payload CMS when REACT_APP_EGD_SOURCE=payload.
 *
 * @param {'en' | 'fr'} locale
 * @param {{ enabled?: boolean }} [options]
 * @returns {{
 *   gameList: import('src/utils/mapEgdGame').EgdGameCard[],
 *   jackpotList: import('src/utils/mapEgdJackpot').EgdJackpotMeter[],
 *   loading: boolean,
 *   error: string | null,
 *   refetch: () => Promise<void>,
 *   isPayloadSource: boolean,
 * }}
 */
export function useEgdHub(locale, options = {}) {
  const { enabled = true } = options;
  const isPayloadSource = isPayloadEgdSource();
  const shouldFetch = enabled && isPayloadSource;
  const [gameList, setGameList] = useState([]);
  const [jackpotList, setJackpotList] = useState([]);
  const [loading, setLoading] = useState(shouldFetch);
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    if (!shouldFetch) return;

    setLoading(true);
    setError(null);

    try {
      const [games, jackpots] = await Promise.all([
        fetchEgdGames(locale),
        fetchEgdJackpotMeters(),
      ]);
      setGameList(games);
      setJackpotList(jackpots);
    } catch (err) {
      console.error("Error fetching EGD data from Payload:", err);
      setError(err?.message || "Failed to load EGD data");
      setGameList([]);
      setJackpotList([]);
    } finally {
      setLoading(false);
    }
  }, [locale, shouldFetch]);

  useEffect(() => {
    if (!shouldFetch) {
      setLoading(false);
      return;
    }
    refetch();
  }, [shouldFetch, refetch]);

  return {
    gameList,
    jackpotList,
    loading,
    error,
    refetch,
    isPayloadSource,
  };
}
